import { queryKeys } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import {
  useTenantEnvironmentsQuery,
  type TenantEnvironmentCell,
  type TenantEnvironmentPin,
} from "@/api/tenants";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/ui/Badge";
import { Card } from "@/ui/Card";

/**
 * The declared runtime per tenant and environment (#1173), under the Tenants
 * list. Each line shows the brain's short commit, the base revision and the
 * overlay, and "differs" where it is not what Dev shows.
 */

const COLUMNS = ["dev", "staging", "prod"] as const;
const ENVIRONMENT_LABEL: Record<string, string> = {
  dev: "Dev",
  staging: "Staging",
  prod: "Prod",
};

const FIELDS = ["brain", "base", "overlay"] as const;
type Field = (typeof FIELDS)[number];
const FIELD_LABEL: Record<Field, string> = {
  brain: "Brain",
  base: "Base",
  overlay: "Overlay",
};

/** A pack's revision, or the first 12 hex chars of its digest when it has none. */
const shortRevision = (pin: TenantEnvironmentPin) =>
  pin.revision ?? pin.ociDigest.replace(/^sha256:/, "").slice(0, 12);

/** The text a clinician compares across environments; null when the field is unset. */
function fieldText(cell: TenantEnvironmentCell, field: Field): string | null {
  if (field === "brain")
    return cell.brain
      ? (cell.brain.commit?.slice(0, 7) ?? cell.brain.tag)
      : null;
  if (field === "base") return cell.base ? shortRevision(cell.base) : null;
  return cell.overlay
    ? `${cell.overlay.name} ${shortRevision(cell.overlay)}`
    : null;
}

function EnvironmentCell({
  cell,
  dev,
}: {
  cell: TenantEnvironmentCell | undefined;
  dev: TenantEnvironmentCell | undefined;
}) {
  if (cell === undefined) {
    return (
      <span className="text-xs text-muted-foreground">
        Not in this environment
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {FIELDS.map((field) => {
        const text = fieldText(cell, field);
        const differs = dev !== undefined && text !== fieldText(dev, field);
        return (
          <div
            key={field}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
          >
            <span className="w-14 text-xs text-muted-foreground">
              {FIELD_LABEL[field]}
            </span>
            {text === null ? (
              <span className="text-xs text-muted-foreground">
                {field === "overlay" ? "No overlay" : "Not declared"}
              </span>
            ) : (
              <code className="font-mono text-xs">{text}</code>
            )}
            {differs ? (
              <Badge tone="warning" title="Differs from Dev">
                differs
              </Badge>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function TenantEnvironmentsTable() {
  const query = useTenantEnvironmentsQuery();

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-base font-semibold">Runtime per environment</h3>
        <span className="text-xs text-muted-foreground">
          What each tenant is declared to run in Dev, Staging and Prod.
        </span>
      </div>

      {query.isPending ? (
        <QueryPendingState
          isPaused={query.isPaused}
          failureReason={query.failureReason}
          label="Loading environment declarations"
          errorTitle="Could not read the environment declarations"
          rows={3}
          queryKey={queryKeys.tenantEnvironments}
        />
      ) : query.isError ? (
        <QueryErrorState
          error={query.error}
          subject="Environment declarations"
          title="Could not read the environment declarations"
          description="The Editor Service did not return the environment declarations."
          deniedDescription="You are not authorized to read the environment declarations."
          onRetry={() => void query.refetch()}
        />
      ) : query.data.items.length === 0 ? (
        <Card className="px-3 py-8 text-center text-sm text-muted-foreground">
          No tenants are declared in these environments.
        </Card>
      ) : (
        <>
          <Card className="overflow-x-auto">
            <table className="w-full min-w-[38rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-semibold text-muted-foreground">
                  <th scope="col" className="px-3 py-2.5">
                    Tenant
                  </th>
                  {COLUMNS.map((environment) => (
                    <th key={environment} scope="col" className="px-3 py-2.5">
                      {ENVIRONMENT_LABEL[environment]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((row) => {
                  const dev = row.environments.find(
                    (cell) => cell.environment === "dev",
                  );
                  return (
                    <tr
                      key={row.tenant}
                      className="border-b border-border align-top hover:bg-muted/50"
                    >
                      <th
                        scope="row"
                        dir="auto"
                        className="px-3 py-2 text-left font-semibold whitespace-nowrap"
                      >
                        {row.tenant}
                      </th>
                      {COLUMNS.map((environment) => (
                        <td key={environment} className="px-3 py-2">
                          <EnvironmentCell
                            cell={row.environments.find(
                              (cell) => cell.environment === environment,
                            )}
                            dev={dev}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
          <p className="text-xs text-muted-foreground">
            Read from{" "}
            {query.data.sources
              .map(
                (source) =>
                  `${ENVIRONMENT_LABEL[source.environment] ?? source.environment}: ${source.repo} ${source.path} @ ${source.ref.slice(0, 7)}`,
              )
              .join(" · ")}{" "}
            at {formatDateTime(query.data.readAt)}.
          </p>
        </>
      )}
    </section>
  );
}
